// „Склад" — ТАБЛОТО, началната страница на модула (slug `dashboard`): по една
// кутия за всяка операция от менюто с живото ѝ състояние (чакащи и закъснели
// отчети, незавършени доставки и на колегите, неплатени, изписаното и
// прехвърленото днес, последната инвентаризация по склад, излишъкът от
// продажби, правилата за заместване; моята незавършена чернова — с
// „Продължи") и основните ѝ действия. Кликът върху
// кутията отваря операцията; бутоните правят същото като на началния ѝ екран.
//
// Решетка 3 колони от 1024 px, 2 на таблет, 1 на телефон; клавиши 1–8, стрелки
// между кутиите, Enter отваря (`useDashboardKeyboard`). Всяка кутия се зарежда
// сама (`useDashboardSources`) — бавен или паднал източник не спира другите.
// Над решетката — ЕДНА червена лента само за критичните (незавършена
// инвентаризация, закъснели отчети, задържани и просрочени доставки), до 3 реда.
import { useRef } from "react";
import dayjs from "dayjs";
import { ReloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../hooks/useViewport";
import { InventoryTheme } from "../components/InventoryTheme";
import { KbdChip } from "../components/KbdChip";
import { AttentionBanner } from "../components/list/AttentionBanner";
import { PageHeader } from "../components/list/PageHeader";
import { SECONDARY_BUTTON_CLASS } from "../components/list/buttonStyles";
import { useInventoryShell } from "../components/shell/InventoryShellContext";
import { DraftHomeModals } from "../components/drafts/DraftHomeModals";
import { DRAFT_TEXTS } from "../components/drafts/draftTexts";
import { dashboardColumnsFor } from "./dashboardModel";
import { DashboardBox } from "./components/DashboardBox";
import { useDashboardBoxes } from "./components/useDashboardBoxes";
import { useDashboardAlerts } from "./components/useDashboardAlerts";
import { useDashboardKeyboard } from "./components/useDashboardKeyboard";
import { useDashboardSources } from "./components/useDashboardSources";

// Tailwind чете изходния текст — пълните имена на класовете, не сглобени
const GRID_COLUMNS_CLASS: Record<number, string> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
};

export const InventoryDashboardPage = () => {
  const { t } = useTranslation();
  const { isNarrow, isDesktop } = useViewport();
  const { warehouseId, warehouseName } = useInventoryShell();
  const sources = useDashboardSources();
  const { drafts } = sources;

  const boxes = useDashboardBoxes({
    summaries: sources.summaries,
    drafts,
    warehouseId,
    warehouseName,
  });
  const alerts = useDashboardAlerts(sources.summaries, drafts);

  const gridRef = useRef<HTMLDivElement | null>(null);
  const columns = dashboardColumnsFor({ isNarrow, isDesktop });
  useDashboardKeyboard({
    gridRef,
    columns,
    total: boxes.length,
    onOpenIndex: (index) => boxes[index]?.onOpen(),
  });

  const refreshLabel = t("inventoryPage.refresh");
  const subtitle = [warehouseName, dayjs().format("DD.MM.YYYY")].filter(Boolean).join(" · ");

  return (
    <InventoryTheme>
      <div className="flex h-full flex-col bg-canvas text-ink">
        <PageHeader
          title={t("inventoryPage.dashTitle")}
          subtitle={subtitle}
          right={
            <button
              type="button"
              onClick={sources.refresh}
              disabled={sources.isLocal}
              aria-label={refreshLabel}
              aria-busy={sources.refreshing}
              title={refreshLabel}
              className={`${SECONDARY_BUTTON_CLASS} ${isNarrow ? "!h-[38px] !w-[38px] !px-0" : ""}`}
            >
              <ReloadOutlined spin={sources.refreshing} className="text-[13px] text-body" />
              {!isNarrow && refreshLabel}
            </button>
          }
        />
        <div
          className={`flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto ${
            isNarrow ? "px-3 pb-10 pt-3.5" : "px-7 pb-12 pt-5"
          }`}
        >
          {sources.isLocal && (
            <div className="rounded-xl border border-dashed border-shade-4 px-[18px] py-3 text-[13px] leading-[1.45] text-body">
              {t("inventoryPage.dashLocalOff")}
            </div>
          )}

          <AttentionBanner
            tone="critical"
            label={t("inventoryPage.dashBannerLabel")}
            items={alerts}
          />

          <div
            ref={gridRef}
            role="group"
            aria-label={t("inventoryPage.dashGridAria")}
            className={`grid auto-rows-fr gap-3.5 ${GRID_COLUMNS_CLASS[columns]}`}
          >
            {boxes.map((box, index) => (
              <DashboardBox key={box.slug} box={box} index={index} showKey={!isNarrow} />
            ))}
          </div>

          {/* Подсказка за клавишите — само където има клавиатура */}
          {!isNarrow && (
            <p className="m-0 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[12.5px] leading-[normal] text-muted">
              <KbdChip>1</KbdChip>–<KbdChip>{boxes.length}</KbdChip>
              <span>{t("inventoryPage.dashKeysOpen")}</span>
              <span aria-hidden>·</span>
              <KbdChip>←</KbdChip>
              <KbdChip>↑</KbdChip>
              <KbdChip>↓</KbdChip>
              <KbdChip>→</KbdChip>
              <span>{t("inventoryPage.dashKeysMove")}</span>
              <span aria-hidden>·</span>
              <KbdChip>Enter</KbdChip>
              <span>{t("inventoryPage.dashKeysEnter")}</span>
            </p>
          )}
        </div>

        {/* „+ Нова" при незавършена и заета чернова — същите въпроси като на
            началните екрани на доставка, изписване, инвентаризация, прехвърляне */}
        <DraftHomeModals drafts={drafts.delivery} texts={DRAFT_TEXTS.delivery} />
        <DraftHomeModals drafts={drafts.writeoff} texts={DRAFT_TEXTS.writeoff} />
        <DraftHomeModals drafts={drafts.stocktake} texts={DRAFT_TEXTS.stocktake} />
        <DraftHomeModals drafts={drafts.transfer} texts={DRAFT_TEXTS.transfer} />
      </div>
    </InventoryTheme>
  );
};

export default InventoryDashboardPage;
